import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { T, berechneBasisPct, naechsterProfilSchritt, parseMatchScore, extractPreviewTitle, PLATZHALTER, useLongPress } from '../utils/themeUtils';

function ProfilVollstaendigkeitKarte({ profile, hatProfile, profilLaedt, onClick }) {
  const pct = berechneBasisPct(profile);
  const abgeschlossen = Math.round(pct / 12.5);
  const hint = naechsterProfilSchritt(profile);

  // Karte ausblenden wenn Profil vollständig
  if (!profilLaedt && hatProfile && pct >= 100) return null;

  const keinProfil = !profilLaedt && !hatProfile;

  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
      style={{
        margin: '0 16px 16px',
        borderRadius: 16,
        background: 'rgba(26,56,40,0.07)',
        border: `1.5px solid ${T.accentSoft}`,
        padding: '14px 16px',
        cursor: 'pointer',
        WebkitTapHighlightColor: 'transparent',
        transition: 'opacity 0.15s',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.text, lineHeight: 1.3, marginBottom: 2 }}>
            {keinProfil ? 'Noch kein Profil vorhanden' : 'Bewerbungsprofil vervollständigen'}
          </div>
          {!keinProfil && (
            <div style={{ fontSize: 11, color: T.textMuted, fontFamily: T.mono }}>
              {abgeschlossen} von 8 Feldern ausgefüllt
            </div>
          )}
        </div>
        <div style={{
          fontSize: 15, fontWeight: 700, color: T.accent,
          background: T.accentSoft, borderRadius: 8,
          padding: '3px 8px', fontFamily: T.mono,
          flexShrink: 0, marginLeft: 8,
        }}>
          {keinProfil ? '!' : `${pct}%`}
        </div>
      </div>

      {/* Fortschrittsbalken */}
      {!keinProfil && (
        <div style={{
          height: 5, borderRadius: 99,
          background: T.border, overflow: 'hidden', marginBottom: 10,
        }}>
          <div style={{
            height: '100%', borderRadius: 99,
            width: `${pct}%`,
            background: T.accent,
            transition: 'width 0.4s ease',
          }} />
        </div>
      )}

      {/* Nächster Schritt */}
      {hint && (
        <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 8 }}>
          → {hint}
        </div>
      )}
      {keinProfil && (
        <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 8 }}>
          Erstelle dein Profil um Bewerbungen zu generieren
        </div>
      )}

      {/* CTA */}
      <div style={{ fontSize: 12, fontWeight: 600, color: T.accent }}>
        {keinProfil ? 'Profil erstellen →' : 'Jetzt ausfüllen →'}
      </div>
    </div>
  );
}

function NavStelleCard({ s, markiert, onOpen, isAktiv, th, onContextMenu, isRenaming, onRenameSubmit }) {
  const ausstehend = !s.generiert_am && !s.analyse;
  const hatPlatzhalter = !s.name || PLATZHALTER.includes(s.name.trim());
  const anzeigeName = (ausstehend && hatPlatzhalter) ? extractPreviewTitle(s.text) : s.name;
  const match = ausstehend ? null : (parseMatchScore(s.analyse) ?? s.match_score ?? null);

  const [renameWert, setRenameWert] = useState(s.name || '');
  const cardRef = useRef(null);
  const renameRef = useRef(null);
  const preventClick = useRef(false);
  const prevNameRef = useRef(s.name);
  const [typing, setTyping] = useState(false);
  const [typedText, setTypedText] = useState('');
  const typingTimerRef = useRef(null);

  useEffect(() => {
    const prev = prevNameRef.current;
    prevNameRef.current = s.name;
    if (prev === 'Neue Stellenanzeige' && s.name && s.name !== 'Neue Stellenanzeige') {
      clearInterval(typingTimerRef.current);
      setTyping(true);
      setTypedText('');
      let i = 0;
      const fullName = s.name;
      typingTimerRef.current = setInterval(() => {
        i++;
        setTypedText(fullName.slice(0, i));
        if (i >= fullName.length) {
          clearInterval(typingTimerRef.current);
          setTimeout(() => setTyping(false), 700);
        }
      }, 40);
      return () => clearInterval(typingTimerRef.current);
    }
  }, [s.name]);

  useEffect(() => {
    if (isRenaming) {
      setRenameWert(s.name || '');
      setTimeout(() => { renameRef.current?.focus(); renameRef.current?.select(); }, 50);
    }
  }, [isRenaming, s.name]);

  const longPress = useLongPress(() => {
    if (!cardRef.current) return;
    preventClick.current = true;
    onContextMenu(s, cardRef.current.getBoundingClientRect());
  });

  const handleClick = () => {
    if (preventClick.current) { preventClick.current = false; return; }
    if (!isRenaming) onOpen();
  };

  const handleRenameSubmit = () => {
    const trimmed = renameWert.trim();
    onRenameSubmit(s, trimmed || s.name);
  };

  return (
    <div
      ref={cardRef}
      style={{
        padding: '11px 14px',
        cursor: 'pointer',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        borderLeft: markiert ? '3px solid #FF6600' : '3px solid transparent',
        background: markiert ? 'rgba(255,102,0,0.04)' : 'transparent',
        marginLeft: -3,
      }}
      onClick={handleClick}
      {...longPress}
    >
      {isRenaming ? (
        <input
          ref={renameRef}
          value={renameWert}
          onChange={(e) => setRenameWert(e.target.value)}
          onBlur={handleRenameSubmit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleRenameSubmit();
            if (e.key === 'Escape') onRenameSubmit(s, s.name);
          }}
          onClick={(e) => e.stopPropagation()}
          style={{
            width: '100%', fontSize: 17, fontWeight: 600, color: th.text,
            letterSpacing: -0.3, background: 'transparent', border: 'none',
            borderBottom: `1.5px solid ${th.accent}`, outline: 'none',
            padding: '0 0 2px', fontFamily: "'Geist', -apple-system, sans-serif",
          }}
        />
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            flex: 1, minWidth: 0,
            fontSize: 17,
            fontWeight: isAktiv ? 700 : 500,
            color: ausstehend ? '#c0392b' : (isAktiv ? th.text : th.textMuted),
            letterSpacing: -0.4, lineHeight: 1.25,
            fontStyle: ausstehend ? 'italic' : 'normal',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {typing ? (
              <>
                {typedText}
                <span style={{ animation: 'typing-cursor-blink 0.6s ease infinite', marginLeft: 1, color: '#FF6600', fontWeight: 400 }}>|</span>
              </>
            ) : anzeigeName}
          </div>
          {markiert && (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
              <path d="M9 22H15C20 22 22 20 22 15V9C22 4 20 2 15 2H9C4 2 2 4 2 9V15C2 20 4 22 9 22Z" fill="rgba(255,102,0,0.15)" stroke="#FF6600" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M17 2.43945V12.4195C17 14.3895 15.59 15.1595 13.86 14.1195L12.54 13.3295C12.24 13.1495 11.76 13.1495 11.46 13.3295L10.14 14.1195C8.41 15.1495 7 14.3895 7 12.4195V2.43945" fill="#FF6600" stroke="#FF6600" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </div>
      )}
    </div>
  );
}

const DAY_SHORT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

function BewerbungsrateCard({ stellen }) {
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    d.setHours(0, 0, 0, 0);
    return d;
  });

  const counts = days.map(dayStart => {
    const dayEnd = new Date(dayStart);
    dayEnd.setHours(23, 59, 59, 999);
    return stellen.filter(s => {
      if (!s.erstellt_am) return false;
      const d = new Date(s.erstellt_am);
      return d >= dayStart && d <= dayEnd;
    }).length;
  });

  const total = counts.reduce((a, b) => a + b, 0);
  const maxCount = Math.max(...counts, 1);

  return (
    <div style={{
      margin: '0 16px',
      padding: '22px 22px 28px',
      borderRadius: 24,
      background: '#004D40',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: '0 16px 48px rgba(0,77,64,0.4), 0 4px 16px rgba(0,0,0,0.18)',
    }}>
      {/* Subtle radial highlight */}
      <div style={{
        position: 'absolute', top: -60, right: -40, width: 200, height: 200,
        background: 'radial-gradient(circle, rgba(255,188,0,0.08) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      {/* Eyebrow */}
      <div style={{
        fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase',
        color: 'rgba(254,252,246,0.45)', fontFamily: T.mono,
        fontWeight: 700, marginBottom: 16,
      }}>Letzte 7 Tage</div>

      {/* Headline + Total */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22 }}>
        <div style={{
          fontSize: 32, lineHeight: 1.08,
          fontFamily: "'DM Serif Display', Georgia, serif",
          fontStyle: 'italic',
          color: '#FEFCF6',
          letterSpacing: -0.5,
        }}>
          Bewerbungs-<br />rate
        </div>
        <div style={{
          fontSize: 58, fontWeight: 800, color: '#FFBC00',
          lineHeight: 0.95, letterSpacing: -3,
          fontFamily: "'Geist', -apple-system, sans-serif",
          marginTop: -4,
        }}>
          {total}
        </div>
      </div>

      {/* Bar chart */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 54 }}>
        {counts.map((count, i) => {
          const isToday = i === 6;
          const barPct = count > 0 ? Math.max((count / maxCount) * 100, 20) : 9;
          return (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, height: '100%' }}>
              <div style={{ flex: 1, width: '100%', display: 'flex', alignItems: 'flex-end' }}>
                <div style={{
                  width: '100%',
                  height: `${barPct}%`,
                  borderRadius: 5,
                  background: isToday && count > 0
                    ? '#FFBC00'
                    : count > 0
                    ? 'rgba(254,252,246,0.28)'
                    : 'rgba(254,252,246,0.1)',
                  transition: 'height 0.4s ease',
                }} />
              </div>
              <div style={{
                fontSize: 9, fontFamily: T.mono,
                color: isToday ? 'rgba(254,252,246,0.7)' : 'rgba(254,252,246,0.3)',
                fontWeight: isToday ? 700 : 400,
              }}>
                {DAY_SHORT[days[i].getDay()]}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function KPIRow({ stellen }) {
  const abgeschickt = stellen.filter(s => s.bewerbungs_status === 'abgeschickt').length;
  const offen = stellen.filter(s =>
    !['abgeschickt', 'absage', 'kennenlerngespraech', 'zusage'].includes(s.bewerbungs_status)
  ).length;

  const kpiStyle = {
    flex: 1, padding: '14px 16px 16px',
    background: '#FEFCF6',
    border: `1px solid rgba(27,23,15,0.09)`,
    borderRadius: 18,
    boxShadow: '0 4px 20px rgba(28,24,18,0.08), 0 1px 4px rgba(28,24,18,0.05)',
  };

  return (
    <div style={{ display: 'flex', gap: 10, margin: '12px 16px 0' }}>
      <div style={kpiStyle}>
        <div style={{ fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(28,24,18,0.38)', fontFamily: T.mono, fontWeight: 700, marginBottom: 8 }}>Offen</div>
        <div style={{ fontSize: 32, fontWeight: 800, color: '#1C1812', letterSpacing: -1.5, lineHeight: 1 }}>{offen}</div>
      </div>
      <div style={kpiStyle}>
        <div style={{ fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(28,24,18,0.38)', fontFamily: T.mono, fontWeight: 700, marginBottom: 8 }}>Abgeschickt</div>
        <div style={{ fontSize: 32, fontWeight: 800, color: '#1C1812', letterSpacing: -1.5, lineHeight: 1 }}>{abgeschickt}</div>
      </div>
    </div>
  );
}

const CTX_ITEM = (danger = false) => ({
  display: 'flex', alignItems: 'center', gap: 10,
  width: '100%', padding: '12px 14px', borderRadius: 10,
  background: 'none', border: 'none', cursor: 'pointer',
  fontSize: 14, fontWeight: 500, textAlign: 'left',
  fontFamily: "'Geist', -apple-system, sans-serif",
  color: danger ? '#c0392b' : '#1C1812',
  WebkitTapHighlightColor: 'transparent',
});

function DashboardJobCard({ stelle, onOpen, onDelete, onAbschicken, markiert, onMarkierenToggle, onRename }) {
  const ausstehend = !stelle.generiert_am && !stelle.analyse;
  const hatPlatzhalterName = !stelle.name || PLATZHALTER.includes(stelle.name.trim());
  const anzeigeName = (ausstehend && hatPlatzhalterName) ? extractPreviewTitle(stelle.text) : stelle.name;
  const initial = (anzeigeName || '?').charAt(0).toUpperCase();
  const match = ausstehend ? null : (parseMatchScore(stelle.analyse) ?? stelle.match_score ?? null);
  const daysAgo = stelle.erstellt_am
    ? Math.floor((Date.now() - new Date(stelle.erstellt_am)) / 86400000)
    : 0;
  const datumLabel = daysAgo === 0 ? 'Heute' : daysAgo === 1 ? 'Gestern' : `vor ${daysAgo} Tagen`;
  const statusLabel = stelle.bewerbungs_status === 'unterlagen_angefertigt' ? 'angefertigt'
    : ausstehend ? 'ausstehend' : 'in Bearbeitung';

  const matchColor = match === null ? '#FF6600'
    : match >= 75 ? '#22a06b'
    : match >= 50 ? '#FF6600'
    : '#e2812d';

  const [showCtxMenu, setShowCtxMenu] = useState(false);
  const [ctxPos, setCtxPos] = useState({ top: 0, right: 16 });
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameWert, setRenameWert] = useState('');
  const [flyingOut, setFlyingOut] = useState(false);
  const [btnPressed, setBtnPressed] = useState(false);
  const cardRef = useRef(null);
  const renameRef = useRef(null);

  const isAbschicken = stelle.bewerbungs_status === 'unterlagen_angefertigt';

  useEffect(() => {
    if (isRenaming) {
      setRenameWert(stelle.name || '');
      setTimeout(() => { renameRef.current?.focus(); renameRef.current?.select(); }, 50);
    }
  }, [isRenaming, stelle.name]);

  const longPress = useLongPress(() => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    setCtxPos({ top: rect.top + 70, right: window.innerWidth - rect.right });
    setShowCtxMenu(true);
  });

  const closeCtx = () => setShowCtxMenu(false);

  const handleAbschicken = (e) => {
    e.stopPropagation();
    if (flyingOut) return;
    setBtnPressed(true);
    setTimeout(() => setBtnPressed(false), 200);
    setTimeout(() => setFlyingOut(true), 150);
    setTimeout(() => onAbschicken && onAbschicken(stelle), 650);
  };

  const handleRenameSubmit = () => {
    const trimmed = renameWert.trim();
    if (trimmed && onRename) onRename(stelle, trimmed);
    setIsRenaming(false);
  };

  return (
    <>
      {showCtxMenu && ReactDOM.createPortal(
        <>
          <div
            style={{
              position: 'fixed', inset: 0,
              backdropFilter: 'blur(7px)', WebkitBackdropFilter: 'blur(7px)',
              background: 'rgba(15,12,8,0.32)', zIndex: 998,
            }}
            onTouchStart={closeCtx}
            onMouseDown={closeCtx}
          />
          <div
            style={{
              position: 'fixed', top: ctxPos.top, right: ctxPos.right,
              background: '#FEFCF6', border: '1px solid rgba(27,23,15,0.09)',
              borderRadius: 18, boxShadow: '0 20px 50px rgba(0,0,0,0.22), 0 4px 14px rgba(0,0,0,0.10)',
              minWidth: 220, padding: 6, zIndex: 999,
              animation: 'dropdownIn 0.15s ease',
            }}
            onTouchStart={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '12px 14px 10px' }}>
              <div style={{
                fontSize: 16, fontWeight: 700, color: '#1C1812',
                letterSpacing: -0.3, lineHeight: 1.25,
                fontFamily: "'Geist', -apple-system, sans-serif",
                marginBottom: match !== null ? 10 : 0,
              }}>
                {anzeigeName || 'Stelle'}
              </div>
              {match !== null && (
                <>
                  <div style={{ height: 4, borderRadius: 999, background: 'rgba(27,23,15,0.1)', overflow: 'hidden', marginBottom: 5 }}>
                    <div style={{ height: '100%', width: `${match}%`, background: matchColor, borderRadius: 999 }} />
                  </div>
                  <div style={{ fontSize: 11, fontFamily: "'Montserrat', sans-serif", color: matchColor, fontWeight: 700 }}>
                    {match}% Übereinstimmung
                  </div>
                </>
              )}
              {ausstehend && (
                <div style={{ fontSize: 11, color: '#b43c28', fontFamily: "'Montserrat', sans-serif", fontWeight: 600 }}>
                  Analyse ausstehend
                </div>
              )}
            </div>
            <div style={{ height: 1, background: 'rgba(27,23,15,0.07)', margin: '0 6px 4px' }} />

            <button style={CTX_ITEM(false)} onClick={() => { onMarkierenToggle && onMarkierenToggle(stelle.id); closeCtx(); }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 22H15C20 22 22 20 22 15V9C22 4 20 2 15 2H9C4 2 2 4 2 9V15C2 20 4 22 9 22Z" stroke={markiert ? '#FF6600' : '#1C1812'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M17 2.43945V12.4195C17 14.3895 15.59 15.1595 13.86 14.1195L12.54 13.3295C12.24 13.1495 11.76 13.1495 11.46 13.3295L10.14 14.1195C8.41 15.1495 7 14.3895 7 12.4195V2.43945" stroke={markiert ? '#FF6600' : '#1C1812'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {markiert ? 'Pin entfernen' : 'Anpinnen'}
            </button>

            <button style={CTX_ITEM(false)} onClick={() => { closeCtx(); setTimeout(() => setIsRenaming(true), 80); }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5.53999 19.5196C4.92999 19.5196 4.35999 19.3096 3.94999 18.9196C3.42999 18.4296 3.17999 17.6896 3.26999 16.8896L3.63999 13.6496C3.70999 13.0396 4.07999 12.2296 4.50999 11.7896L12.72 3.09956C14.77 0.929561 16.91 0.869561 19.08 2.91956C21.25 4.96956 21.31 7.10956 19.26 9.27956L11.05 17.9696C10.63 18.4196 9.84999 18.8396 9.23999 18.9396L6.01999 19.4896C5.84999 19.4996 5.69999 19.5196 5.53999 19.5196ZM15.93 2.90956C15.16 2.90956 14.49 3.38956 13.81 4.10956L5.59999 12.8096C5.39999 13.0196 5.16999 13.5196 5.12999 13.8096L4.75999 17.0496C4.71999 17.3796 4.79999 17.6496 4.97999 17.8196C5.15999 17.9896 5.42999 18.0496 5.75999 17.9996L8.97999 17.4496C9.26999 17.3996 9.74999 17.1396 9.94999 16.9296L18.16 8.23956C19.4 6.91956 19.85 5.69956 18.04 3.99956C17.24 3.22956 16.55 2.90956 15.93 2.90956Z" fill="#1C1812"/>
                <path d="M17.3399 10.9508C17.3199 10.9508 17.2899 10.9508 17.2699 10.9508C14.1499 10.6408 11.6399 8.27083 11.1599 5.17083C11.0999 4.76083 11.3799 4.38083 11.7899 4.31083C12.1999 4.25083 12.5799 4.53083 12.6499 4.94083C13.0299 7.36083 14.9899 9.22083 17.4299 9.46083C17.8399 9.50083 18.1399 9.87083 18.0999 10.2808C18.0499 10.6608 17.7199 10.9508 17.3399 10.9508Z" fill="#1C1812"/>
                <path d="M21 22.75H3C2.59 22.75 2.25 22.41 2.25 22C2.25 21.59 2.59 21.25 3 21.25H21C21.41 21.25 21.75 21.59 21.75 22C21.75 22.41 21.41 22.75 21 22.75Z" fill="#1C1812"/>
              </svg>
              Umbenennen
            </button>

            <div style={{ height: 1, background: 'rgba(27,23,15,0.07)', margin: '4px 6px' }} />

            <button style={CTX_ITEM(true)} onClick={() => { closeCtx(); onDelete && onDelete(stelle); }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 5.98047C17.67 5.65047 14.32 5.48047 10.98 5.48047C9 5.48047 7.02 5.58047 5.04 5.78047L3 5.98047" stroke="#c0392b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M8.5 4.97L8.72 3.66C8.88 2.71 9 2 10.69 2H13.31C15 2 15.13 2.75 15.28 3.67L15.5 4.97" stroke="#c0392b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M18.85 9.14062L18.2 19.2106C18.09 20.7806 18 22.0006 15.21 22.0006H8.79002C6.00002 22.0006 5.91002 20.7806 5.80002 19.2106L5.15002 9.14062" stroke="#c0392b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10.33 16.5H13.66" stroke="#c0392b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9.5 12.5H14.5" stroke="#c0392b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Löschen
            </button>
          </div>
        </>,
        document.body
      )}

      {isRenaming && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
            background: 'rgba(15,12,8,0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 24px',
          }}
          onTouchStart={handleRenameSubmit}
          onMouseDown={handleRenameSubmit}
        >
          <div
            style={{
              background: '#FEFCF6', borderRadius: 18, padding: '20px 18px',
              width: '100%', boxShadow: '0 20px 50px rgba(0,0,0,0.22)',
            }}
            onTouchStart={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(28,24,18,0.4)', marginBottom: 12, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: "'Montserrat', sans-serif" }}>
              Umbenennen
            </div>
            <input
              ref={renameRef}
              value={renameWert}
              onChange={(e) => setRenameWert(e.target.value)}
              onBlur={handleRenameSubmit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRenameSubmit();
                if (e.key === 'Escape') setIsRenaming(false);
              }}
              style={{
                width: '100%', fontSize: 18, fontWeight: 700, color: '#1C1812',
                letterSpacing: -0.3, background: 'transparent', border: 'none',
                borderBottom: '2px solid #FF6600', outline: 'none',
                padding: '0 0 6px', fontFamily: "'Geist', -apple-system, sans-serif",
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>,
        document.body
      )}

      {/* Fly-out wrapper: collapses height after the card slides away */}
      <div
        style={{
          overflow: 'hidden',
          maxHeight: flyingOut ? 0 : 300,
          transition: flyingOut ? 'max-height 0.25s ease 0.38s' : 'none',
        }}
      >
        <div
          style={{
            transform: flyingOut ? 'translateX(115%) rotate(2deg)' : 'translateX(0) rotate(0deg)',
            opacity: flyingOut ? 0 : 1,
            transition: flyingOut
              ? 'transform 0.38s cubic-bezier(0.55, 0, 0.95, 0.45), opacity 0.28s ease'
              : 'none',
          }}
        >
          <div
            ref={cardRef}
            {...longPress}
            style={{
              background: markiert ? 'rgba(255,102,0,0.03)' : '#FEFCF6',
              border: markiert ? '1px solid rgba(255,102,0,0.30)' : '1px solid rgba(27,23,15,0.09)',
              borderRadius: 20, padding: '16px 16px 14px',
              position: 'relative', userSelect: 'none',
              WebkitTapHighlightColor: 'transparent',
              boxShadow: markiert
                ? '0 2px 12px rgba(255,102,0,0.10)'
                : '0 2px 12px rgba(28,24,18,0.06)',
            }}
          >
            {markiert && (
              <div style={{
                position: 'absolute', top: 12, right: 14,
                display: 'flex', alignItems: 'center', gap: 4,
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 22H15C20 22 22 20 22 15V9C22 4 20 2 15 2H9C4 2 2 4 2 9V15C2 20 4 22 9 22Z" fill="rgba(255,102,0,0.12)" stroke="#FF6600" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M17 2.43945V12.4195C17 14.3895 15.59 15.1595 13.86 14.1195L12.54 13.3295C12.24 13.1495 11.76 13.1495 11.46 13.3295L10.14 14.1195C8.41 15.1495 7 14.3895 7 12.4195V2.43945" fill="#FF6600" stroke="#FF6600" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <div style={{
                width: 42, height: 42, borderRadius: 12, flexShrink: 0,
                background: '#004D40', color: '#FFBC00',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 16,
              }}>
                {initial}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#1C1812', letterSpacing: -0.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {anzeigeName || 'Unbenannte Stelle'}
                </div>
                <div style={{ fontSize: 12, color: 'rgba(28,24,18,0.5)', marginTop: 2 }}>
                  {datumLabel} · {statusLabel}
                </div>
              </div>
            </div>

            <button
              onClick={isAbschicken ? handleAbschicken : onOpen}
              onTouchStart={isAbschicken ? (e) => e.stopPropagation() : undefined}
              style={{
                width: '100%', height: 44, borderRadius: 30, border: 'none',
                background: btnPressed ? '#00695C' : '#004D40',
                color: '#FEFCF6',
                fontFamily: 'inherit', fontSize: 14, fontWeight: 600,
                cursor: 'pointer', letterSpacing: -0.2,
                WebkitTapHighlightColor: 'transparent',
                boxShadow: '0 4px 16px rgba(0,77,64,0.25)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transform: btnPressed ? 'scale(0.94)' : 'scale(1)',
                transition: 'transform 0.15s ease, background 0.15s ease',
              }}
            >
              {isAbschicken ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.51002 4.23062L18.07 8.51062C21.91 10.4306 21.91 13.5706 18.07 15.4906L9.51002 19.7706C3.75002 22.6506 1.40002 20.2906 4.28002 14.5406L5.15002 12.8106C5.37002 12.3706 5.37002 11.6406 5.15002 11.2006L4.28002 9.46062C1.40002 3.71062 3.76002 1.35062 9.51002 4.23062Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M5.43994 12H10.8399" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Abschicken
                </>
              ) : (
                ausstehend ? 'Generieren →' : 'Öffnen →'
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function JobCard({ stelle, onOpen, onDelete }) {
  const ausstehend = !stelle.generiert_am && !stelle.analyse;
  const hatPlatzhalterName = !stelle.name || PLATZHALTER.includes(stelle.name.trim());
  const anzeigeName = (ausstehend && hatPlatzhalterName) ? extractPreviewTitle(stelle.text) : stelle.name;
  const initial = (anzeigeName || '?').charAt(0).toUpperCase();
  const match = ausstehend ? null : (parseMatchScore(stelle.analyse) ?? stelle.match_score ?? null);
  const daysAgo = stelle.erstellt_am
    ? Math.floor((Date.now() - new Date(stelle.erstellt_am)) / 86400000)
    : 0;
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const longPress = useLongPress(() => setShowDeleteConfirm(true));

  return (
    <div
      onClick={!showDeleteConfirm ? onOpen : undefined}
      {...longPress}
      style={{
        display: 'block', width: '100%', textAlign: 'left',
        background: ausstehend ? 'rgba(180,60,40,0.07)' : T.surface,
        border: showDeleteConfirm ? '1.5px solid rgba(200,40,40,0.5)' : ausstehend ? '1px solid rgba(180,60,40,0.22)' : `1px solid ${T.border}`,
        borderRadius: 20, padding: '16px 18px', margin: 0,
        cursor: 'pointer', fontFamily: 'inherit',
        WebkitTapHighlightColor: 'transparent',
        opacity: ausstehend ? 0.85 : 1,
        position: 'relative', userSelect: 'none',
      }}
    >
      {showDeleteConfirm && (
        <div style={{
          position: 'absolute', inset: 0, borderRadius: 19,
          background: 'rgba(200,40,30,0.92)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 10, zIndex: 5,
        }}>
          <div style={{ color: '#fff', fontSize: 13, fontWeight: 600, textAlign: 'center', padding: '0 12px' }}>
            Stelle löschen?
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(false); }}
              style={{
                padding: '7px 18px', borderRadius: 999, border: '1.5px solid rgba(255,255,255,0.4)',
                background: 'transparent', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                fontFamily: 'inherit', WebkitTapHighlightColor: 'transparent',
              }}
            >Abbrechen</button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete && onDelete(stelle); }}
              style={{
                padding: '7px 18px', borderRadius: 999, border: 'none',
                background: '#fff', color: '#c0392b', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                fontFamily: 'inherit', WebkitTapHighlightColor: 'transparent',
              }}
            >Löschen</button>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10, flexShrink: 0,
          background: ausstehend ? 'rgba(180,60,40,0.18)' : T.accent,
          color: ausstehend ? '#b43c28' : T.accentText,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 600, fontSize: 15,
        }}>
          {initial}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
            <div style={{ fontSize: 12, color: ausstehend ? '#b43c28' : T.textMuted, fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 8 }}>
              {ausstehend ? 'Nicht generiert' : (stelle.empfaenger_unternehmen || stelle.name)}
            </div>
            <div style={{ fontFamily: T.mono, fontSize: 10, color: T.textDim, whiteSpace: 'nowrap' }}>
              {daysAgo === 0 ? 'Heute' : `vor ${daysAgo}${daysAgo === 1 ? ' Tag' : ' Tagen'}`}
            </div>
          </div>
          <div style={{
            fontSize: 15, fontWeight: 600,
            color: ausstehend ? '#c0392b' : T.text,
            letterSpacing: -0.2, lineHeight: 1.3, marginBottom: 10,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            fontStyle: ausstehend ? 'italic' : 'normal',
          }}>
            {anzeigeName}
          </div>
          {ausstehend ? (
            <div style={{
              display: 'inline-block', padding: '3px 9px', borderRadius: 999,
              background: 'rgba(180,60,40,0.12)', color: '#b43c28',
              fontSize: 11, fontWeight: 600, letterSpacing: 0.2,
            }}>
              ⚠ Ausstehend
            </div>
          ) : match !== null && (
            <>
              <div style={{ position: 'relative', height: 3, borderRadius: 999, background: T.border, overflow: 'hidden', marginBottom: 4 }}>
                <div style={{
                  position: 'absolute', left: 0, top: 0, bottom: 0, borderRadius: 999,
                  background: T.accent, width: `${match}%`, transition: 'width 0.4s ease',
                }} />
              </div>
              <div style={{ fontSize: 11, color: T.textDim, textAlign: 'right', fontFamily: T.mono }}>
                {match}% match
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function IslandDots({ active }) {
  return (
    <div style={{
      position: 'fixed', bottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
      left: '50%', transform: 'translateX(-50%)',
      display: 'flex', gap: 6, zIndex: 50, pointerEvents: 'none',
    }}>
      {[0, 1].map(i => (
        <div key={i} style={{
          width: i === active ? 20 : 6,
          height: 6, borderRadius: 999,
          background: i === active ? T.accent : T.border,
          transition: 'width 0.25s ease, background 0.25s ease',
        }} />
      ))}
    </div>
  );
}


export { ProfilVollstaendigkeitKarte, NavStelleCard, BewerbungsrateCard, KPIRow, DashboardJobCard, JobCard, IslandDots };
